import { Component, computed, effect, ElementRef, input, model, output, signal, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { CheckboxComponent } from '../inputs/checkbox/checkbox.component';
import { RadioButtonComponent } from '../inputs/radio-button/radio-button.component';
import { IconComponent } from '../icon/icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownOption } from '../../models/dropdown-option.model';

export type dropDownAlignment = 'left' | 'right' | 'bottom' | 'top';

@Component({
  selector: 'work-ui-dropdown-list',
  templateUrl: './dropdown-list.component.html',
  styleUrls: ['./dropdown-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    CheckboxComponent,
    RadioButtonComponent,
    IconComponent,
    TranslateModule,
  ],
})
export class DropdownListComponent {
  protected listItems = viewChildren<ElementRef>('listItem');
  public options = model<DropdownOption[]>([]);
  public activeStringValue = model<string>('');
  public initialOption = input<DropdownOption | undefined>(undefined);
  public dropDownOpen = model(false);
  public firstOptionActiveOnInit = input(true);
  public preventActiveHighlighting = input(false);
  public dynamicDropdownOptions = input(false);
  public withCheckboxes = input(false);
  /** Hides the "Schliessen" action (used when the list is embedded open/inline — there's nothing to close). */
  public hideCloseAction = input(false);
  /** Applies (emits) on every checkbox/radio change instead of waiting for an "Anwenden" button. */
  public immediateApply = input(false);
  public singleSelectGroups = input<string[]>([]);
  public listTitle = input('');
  public optionSelected = output<DropdownOption>();
  public optionRemoved = output<DropdownOption>();
  public selectedCheckboxOptionsChange = output<void>();

  public checkboxOptionsState = signal<DropdownOption[]>([]);
  // To keep track of selected index
  protected selectedIndex = signal(0);
  // For highlighting the best match
  protected bestMatch: DropdownOption | undefined;
  protected checkboxesSelectedAll = false;
  protected checkboxesSelectedNone = false;
  protected numberOfSelectedCheckboxes = 0;
  protected checkboxesGroupedOptions: { [key: string]: DropdownOption[] } = {};
  protected checkboxesGroupsState: { [key: string]: { allSelected: boolean } } = {};
  protected checkboxesHasChanges = signal(false);
  protected checkboxesUseGroups = signal(false);
  protected searchFilteredOptions = computed(() => {
    const searchValue = this.activeStringValue();
    if (!searchValue || this.dynamicDropdownOptions()) {
      return this.options();
    }
    const searchLowerCase = searchValue.toLowerCase();
    return this.options().filter((option) => this.matchesSearch(option, searchLowerCase));
  });
  private checkboxesInitialOptionsState = signal<DropdownOption[]>([]);
  private activeIndex = signal(0);

  private firstOptionActiveOnInitEffect = effect(() => {
    if (this.firstOptionActiveOnInit()) {
      this.activeIndex.set(0);
      this.selectedIndex.set(0);
    } else {
      this.activeIndex.set(-1);
      this.selectedIndex.set(-1);
    }
  });

  private activeStringValueEffect = effect(() => {
    const searchValue = this.activeStringValue();
    if (searchValue && !this.dynamicDropdownOptions()) {
      this.findBestMatch();
    }
    if (!searchValue) {
      this.bestMatch = undefined;
      this.dropDownOpen.set(true);
    }
  });

  private checkboxOptionsInitEffect = effect(() => {
    if (this.withCheckboxes() && this.options().length > 0) {
      // Deep copy options to checkboxOptionsState
      this.checkboxOptionsState.set(JSON.parse(JSON.stringify(this.options())));
      this.checkboxesInitialOptionsState.set(JSON.parse(JSON.stringify(this.options())));
    }
  });

  private checkboxOptionsStateEffect = effect(() => {
    if (this.withCheckboxes() && this.checkboxOptionsState().length > 0) {
      this.completeGroupNames();
      this.groupOptions();
      this.checkGroupAllSelected();
      this.numberOfSelectedCheckboxes = this.options().filter((option) => option.selected === true).length;
      this.checkboxesSelectedAll = this.options().every((option) => option.selected === true);
      this.checkboxesSelectedNone = this.options().every((option) => option.selected === false);
    }
  });

  // called in parent component
  public onKeyDown(event: KeyboardEvent) {
    const filteredOptions = this.searchFilteredOptions();
    if (filteredOptions.length === 0) return;

    const currentIndex = this.selectedIndex();

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = currentIndex < filteredOptions.length - 1 ? currentIndex + 1 : 0;
        this.selectedIndex.set(nextIndex);
        this.updateSelectedOption();
        this.scrollActiveItemIntoView();
        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredOptions.length - 1;
        this.selectedIndex.set(prevIndex);
        this.updateSelectedOption();
        this.scrollActiveItemIntoView();
        break;
      }

      case 'Enter':
        event.preventDefault();
        if (currentIndex >= 0 && currentIndex < filteredOptions.length) {
          const selectedOption = filteredOptions[currentIndex];
          if (!selectedOption.disabled) {
            this.selectOption(selectedOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.selectedIndex.set(-1);
        this.updateSelectedOption();
        break;
    }
  }

  public resetSelection() {
    this.checkboxOptionsState.set(JSON.parse(JSON.stringify(this.checkboxesInitialOptionsState())));
    this.checkboxesUpdateHasChanges();
  }

  protected selectOption(option: DropdownOption) {
    this.optionSelected.emit(option);
    this.activeIndex.set(this.options().findIndex((arrayOption) => arrayOption === option));
    this.selectedIndex.set(this.activeIndex());
    this.dropDownOpen.set(false);
  }

  protected isSingleSelectGroup(group: string | undefined): boolean {
    return !!group && this.singleSelectGroups().includes(group);
  }

  /** While true, batch operations suppress the per-option immediate-apply emit (one emit at the end instead). */
  private batchingCheckboxChanges = false;

  private maybeEmitImmediateApply(): void {
    if (this.immediateApply() && !this.batchingCheckboxChanges) {
      this.selectedCheckboxOptionsChange.emit();
    }
  }

  protected selectCheckboxOption(option: DropdownOption) {
    // In single-select groups, picking a new option first deselects all siblings in the same group.
    if (!option.selected && this.isSingleSelectGroup(option.group)) {
      this.checkboxOptionsState().forEach((sibling) => {
        if (sibling !== option && sibling.group === option.group && sibling.selected) {
          sibling.selected = false;
          this.numberOfSelectedCheckboxes -= 1;
          this.optionRemoved.emit(sibling);
        }
      });
    }
    if (option.selected) {
      this.numberOfSelectedCheckboxes -= 1;
      option.selected = false;
      this.optionRemoved.emit(option);
    } else {
      this.numberOfSelectedCheckboxes += 1;
      option.selected = true;
      this.optionSelected.emit(option);
    }
    this.checkboxesUpdateHasChanges();
    this.checkboxesSelectedAll = this.options().every((option) => option.selected === true);
    this.checkboxesSelectedNone = this.options().every((option) => option.selected === false);
    this.checkGroupAllSelected();
    this.maybeEmitImmediateApply();
  }

  protected actionForAllCheckboxes(action: 'select' | 'deselect') {
    this.numberOfSelectedCheckboxes = action === 'deselect' ? this.options().length : 0;
    this.batchingCheckboxChanges = true;
    this.checkboxOptionsState().forEach((option) => {
      // "Select all" must not violate single-select group uniqueness; "deselect all" applies to everything.
      if (action === 'select' && this.isSingleSelectGroup(option.group)) {
        return;
      }
      option.selected = action !== 'select';
      this.selectCheckboxOption(option);
    });
    this.batchingCheckboxChanges = false;
    this.checkboxOptionsState.set([...this.checkboxOptionsState()]);
    this.checkGroupAllSelected();
    this.maybeEmitImmediateApply();
  }

  protected acceptSelectedCheckboxOptions() {
    this.toggleCheckboxesDropDown();
    if (this.checkboxesHasChanges()) {
      this.selectedCheckboxOptionsChange.emit();
    }
  }

  protected getGroupObjectKeys(obj: any): string[] {
    // Assure 'Weitere' is last array element
    const keys = Object.keys(obj);
    const indexOfWeitere = keys.indexOf('WEITERE');
    if (indexOfWeitere !== -1) {
      keys.splice(indexOfWeitere, 1);
      keys.push('WEITERE');
    }
    return keys;
  }

  protected toggleAllGroupOptions(group: string) {
    // Single-select groups have no group-level toggle (selecting all would violate uniqueness).
    if (this.isSingleSelectGroup(group)) {
      return;
    }
    const allSelected = this.checkboxesGroupedOptions[group].every((option) => option.selected);
    const selectedOptionsCountInGroup = this.checkboxesGroupedOptions[group].filter((option) => option.selected).length;
    this.numberOfSelectedCheckboxes -= allSelected || selectedOptionsCountInGroup === 0 ? 0 : selectedOptionsCountInGroup;
    this.batchingCheckboxChanges = true;
    this.checkboxOptionsState().forEach((option) => {
      if (option.group !== group) return;
      option.selected = allSelected;
      this.selectCheckboxOption(option);
    });
    this.batchingCheckboxChanges = false;
    this.checkboxOptionsState.set([...this.checkboxOptionsState()]);
    this.maybeEmitImmediateApply();
  }

  private checkGroupAllSelected() {
    this.getGroupObjectKeys(this.checkboxesGroupedOptions).forEach((group) => {
      this.checkboxesGroupsState[group] = {
        allSelected: this.checkboxesGroupedOptions[group].every((option) => option.selected),
      };
    });
  }

  private toggleCheckboxesDropDown() {
    if (this.options().length > 0) {
      if (!this.dropDownOpen()) {
        this.checkGroupAllSelected();
        if (this.selectedIndex() > -1) {
          this.selectedIndex.set(this.activeIndex());
        }
        // Store a deep copy of the options array to ensure the initial state is not modified later
        this.checkboxesInitialOptionsState.set(JSON.parse(JSON.stringify(this.searchFilteredOptions())));
      }
      this.dropDownOpen.set(!this.dropDownOpen());
    }
  }

  private matchesSearch(option: DropdownOption, searchLowerCase: string): boolean {
    return `${option.label} ${option.searchText ?? ''}`.toLowerCase().includes(searchLowerCase);
  }

  private findBestMatch() {
    const searchValue = this.activeStringValue();
    if (searchValue) {
      const searchLowerCase = `${searchValue}`.toLowerCase();
      this.bestMatch = this.options().find((option) => this.matchesSearch(option, searchLowerCase));
      if (this.bestMatch) {
        if (searchValue !== this.bestMatch.label) {
          this.dropDownOpen.set(true);
        }
        this.selectedIndex.set(this.searchFilteredOptions().findIndex((option) => option.value === this.bestMatch?.value));
        // avoid timing issues
        setTimeout(() => {
          this.scrollActiveItemIntoView();
        });
      }
    } else {
      this.bestMatch = undefined;
    }
  }

  private groupOptions() {
    // reset options when reopening the dropdown
    this.checkboxesGroupedOptions = {};
    // fill the options container
    this.checkboxOptionsState().forEach((option) => {
      const groupName = option.group || 'WEITERE';
      if (!this.checkboxesGroupedOptions[groupName]) {
        this.checkboxesGroupedOptions[groupName] = [];
      }
      this.checkboxesGroupedOptions[groupName].push(option);
    });

    // Determine if we should use groups or not (based on the presence of any group other than 'none')
    this.checkboxesUseGroups.set(Object.keys(this.checkboxesGroupedOptions).some((groupName) => groupName !== 'WEITERE'));
  }

  private updateSelectedOption() {
    // Update bestMatch
    this.bestMatch = this.searchFilteredOptions()[this.selectedIndex()];
  }

  private scrollActiveItemIntoView() {
    const item = this.listItems()?.[this.selectedIndex()];
    if (!item) {
      return;
    }
    item.nativeElement.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
    });
  }

  // Method to deeply compare two arrays of DropdownOption objects
  private checkboxesUpdateHasChanges() {
    const arr1 = this.checkboxOptionsState();
    const arr2 = this.checkboxesInitialOptionsState();
    if (arr1.length !== arr2.length) {
      this.checkboxesHasChanges.set(true);
      return;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i].selected !== arr2[i].selected) {
        this.checkboxesHasChanges.set(true);
        break;
      } else this.checkboxesHasChanges.set(false);
    }
  }

  private completeGroupNames() {
    const options = this.checkboxOptionsState();
    const needsCompletion = options.some((option) => option.group === undefined);

    if (needsCompletion) {
      const completed = options.map(option => ({
        ...option,
        group: option.group ?? 'WEITERE',
      }));
      this.checkboxOptionsState.set(completed);
    }
  }
}
